<template>
  <div
    class="og-chatbot-widget"
    :class="[
      { 'og-chatbot-widget--sidebar': layout === 'sidebar' },
      { 'og-chatbot-widget--embedded': embedded },
    ]"
  >
    <ChatbotButton
      v-if="!embedded"
      :is-open="isOpen"
      :layout="layout"
      :label="isOpen ? 'Fechar chat' : 'Abrir chat'"
      @click="toggle"
    />
    <transition name="og-chatbot-fade">
      <ChatbotWindow
        v-if="isOpen || embedded"
        :layout="layout"
        :embedded="embedded"
        :messages="messages"
        :loading="loading"
        :title="title"
        :placeholder="placeholder"
        :theme="currentTheme"
        :show-theme-toggle="true"
        :show-close-button="embedded"
        :conversa-encerrada="conversaEncerrada"
        :conversa-encerrada-texto="conversaEncerradaBannerTexto"
        :chamado-desk-link="chamadoDeskLink"
        :chamado-desk-link-texto="chamadoDeskLinkTexto"
        :nova-conversa-botao-texto="novaConversaBotaoTexto"
        :show-nova-conversa-link-discreto="showNovaConversaLinkDiscreto"
        :reset-key="conversationResetKey"
        @send="onSend"
        @close="onClose"
        @toggle-theme="toggleTheme"
        @nova-conversa="iniciarNovaConversa"
      />
    </transition>
  </div>
</template>

<script>
import ChatbotButton from './ChatbotButton.vue';
import ChatbotWindow from './ChatbotWindow.vue';

export default {
  name: 'ChatbotWidget',
  components: {
    ChatbotButton,
    ChatbotWindow,
  },
  props: {
    welcomeMessage: {
      type: String,
      default: 'Olá! Sou a Elô, a atendente de IA da Eloca. Como posso ajudar você hoje?',
    },
    apiUrl: {
      type: String,
      default: '',
    },
    apiToken: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: 'Elô',
    },
    placeholder: {
      type: String,
      default: 'Digite sua mensagem...',
    },
    theme: {
      type: String,
      default: 'light',
      validator: (v) => ['light', 'dark'].includes(v),
    },
    chatEndpoint: {
      type: String,
      default: '/api/chat-dify',
    },
    layout: {
      type: String,
      default: 'popup',
      validator: (v) => ['popup', 'sidebar'].includes(v),
    },
    embedded: {
      type: Boolean,
      default: false,
    },
    startOpen: {
      type: Boolean,
      default: false,
    },
    batchDelayMs: {
      type: Number,
      default: 5000,
    },
    conversaEncerradaTexto: {
      type: String,
      default: 'Esta conversa foi encerrada.',
    },
    novaConversaBotaoTexto: {
      type: String,
      default: 'Iniciar nova conversa',
    },
    showNovaConversaLinkDiscreto: {
      type: Boolean,
      default: true,
    },
    pusherHabilitado: {
      type: Boolean,
      default: true,
    },
    pusherEventoChatInatividade: {
      type: String,
      default: 'App\\Events\\ElocaChatInatividadeEvent',
    },
    pusherEventoMensagemResposta: {
      type: String,
      default: 'App\\Events\\ElocaChatMensagemRespostaEvent',
    },
    pusherEventoRespostaAssincrona: {
      type: String,
      default: '',
    },
    respostaHttpTimeoutMs: {
      type: Number,
      default: 0,
    },
    pusherRespostaAssincronaTtlMs: {
      type: Number,
      default: 300000,
    },
    conversaEncerradaInatividadeTexto: {
      type: String,
      default:
        'Esta conversa foi encerrada por inatividade (15 minutos). Você pode iniciar uma nova conversa.',
    },
    chamadoDeskLinkTexto: {
      type: String,
      default: 'Abrir chamado no Desk',
    },
  },
  data() {
    const saved = this.getThemeFromCookie();
    const initial = (saved === 'light' || saved === 'dark') ? saved : this.theme;
    return {
      isOpen: this.startOpen,
      messages: [],
      loading: false,
      currentTheme: initial,
      conversationId: null,
      pendingBatch: [],
      pendingBatchTimer: null,
      conversaEncerrada: false,
      conversaEncerradaPorInatividade: false,
      conversationResetKey: 0,
      pusherChatInatividadeHandler: null,
      pusherRespostaAssincronaHandler: null,
      pusherRespostaAssincronaTtlTimer: null,
      aguardandoRespostaPusher: false,
      requisicaoPendenteId: null,
      chamadoDeskLink: null,
    };
  },
  computed: {
    conversaEncerradaBannerTexto() {
      return this.conversaEncerradaPorInatividade
        ? this.conversaEncerradaInatividadeTexto
        : this.conversaEncerradaTexto;
    },
    pusherMensagemRespostaEvento() {
      const a = (this.pusherEventoRespostaAssincrona || '').trim();
      return a || this.pusherEventoMensagemResposta;
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.ensureWelcomeMessage();
      }
      if (this.layout === 'sidebar' && !this.embedded && typeof document !== 'undefined') {
        document.body.classList.toggle('og-chatbot-sidebar-open', val);
      }
    },
  },
  mounted() {
    this.ensureWelcomeMessage();
  },
  methods: {
    ensureWelcomeMessage() {
      if (!(this.isOpen || this.embedded) || this.messages.length > 0) return;
      this.messages.push({ role: 'bot', text: this.welcomeMessage });
    },
    toggle() {
      this.isOpen = !this.isOpen;
    },
    pickChamadoDeskLink(obj) {
      if (!obj || typeof obj !== 'object') return null;
      const raw = obj.chamado_desk_link ?? obj.chamadoDeskLink;
      if (raw == null || typeof raw !== 'string') return null;
      const t = raw.trim();
      return t || null;
    },
    iniciarNovaConversa(payload) {
      const clearMessages = !payload || payload.clearMessages !== false;
      if (this.pendingBatchTimer) {
        clearTimeout(this.pendingBatchTimer);
        this.pendingBatchTimer = null;
      }
      this.pendingBatch = [];
      this.conversationId = null;
      this.conversaEncerrada = false;
      this.conversaEncerradaPorInatividade = false;
      this.chamadoDeskLink = null;
      this.cancelPusherRespostaPendente({ clearTtl: true, keepLoading: false });
      if (clearMessages) {
        this.messages = [{ role: 'bot', text: this.welcomeMessage }];
      }
      this.conversationResetKey += 1;
      this.$emit('nova-conversa');
    },
    onClose() {
      if (this.embedded && window.parent !== window) {
        try {
          window.parent.postMessage({ action: 'og-chatbot-close' }, '*');
        } catch (e) {
          /* ignored */
        }
      } else {
        this.isOpen = false;
      }
    },
    toggleTheme() {
      this.currentTheme = this.currentTheme === 'light' ? 'dark' : 'light';
      this.setThemeCookie(this.currentTheme);
    },
    getThemeFromCookie() {
      if (typeof document === 'undefined') return null;
      const match = document.cookie.match(/og-chatbot-theme=([^;]+)/);
      return match ? decodeURIComponent(match[1].trim()) : null;
    },
    setThemeCookie(value) {
      if (typeof document === 'undefined') return;
      const maxAge = 365 * 24 * 60 * 60;
      const isSecure = typeof window !== 'undefined' && window.location?.protocol === 'https:';
      const sameSite = isSecure ? 'SameSite=None; Secure' : 'SameSite=Lax';
      document.cookie = `og-chatbot-theme=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; ${sameSite}`;
    },
    onSend(text, files = []) {
      if (this.conversaEncerrada) {
        this.iniciarNovaConversa();
      }
      const imageAtts = files
        .filter((f) => f && f.type && f.type.startsWith('image/'))
        .map((f) => ({ type: 'image', url: URL.createObjectURL(f) }));
      const audioAtts = files
        .filter((f) => f && f.type && f.type.startsWith('audio/'))
        .map((f) => ({ type: 'audio', url: URL.createObjectURL(f) }));
      const attachments = [...imageAtts, ...audioAtts];
      const hasOnlyFiles = text === '(arquivo anexado)' && files.length > 0;
      const displayText = hasOnlyFiles && attachments.length > 0 ? '' : text;
      this.messages.push({
        role: 'user',
        text: displayText,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      this.pendingBatch.push({ text, files: files || [] });

      if (this.pendingBatchTimer) {
        clearTimeout(this.pendingBatchTimer);
      }
      const delay = Math.max(0, Number(this.batchDelayMs) || 5000);
      this.pendingBatchTimer = setTimeout(() => {
        this.pendingBatchTimer = null;
        this.flushPendingBatch();
      }, delay);
    },
    combineBatchForApi(batch) {
      const allFiles = batch.flatMap((b) => b.files || []);
      const textParts = batch.map((b) => {
        const t = (b.text || '').trim();
        if (t === '(arquivo anexado)' && (b.files || []).length > 0) {
          return '(arquivo anexado)';
        }
        return t;
      });
      let combinedText = textParts.filter((p) => p).join('\n\n');
      if (!combinedText && allFiles.length > 0) {
        combinedText = '(arquivo anexado)';
      }
      if (!combinedText) {
        combinedText = ' ';
      }
      return { combinedText, allFiles };
    },
    async flushPendingBatch() {
      if (!this.pendingBatch.length) return;
      const batch = this.pendingBatch.splice(0, this.pendingBatch.length);
      const { combinedText, allFiles } = this.combineBatchForApi(batch);

      this.loading = true;
      const httpMs = this.respostaHttpTimeoutMs;
      const useAbort = httpMs == null || Number(httpMs) > 0;
      const httpTimeout = useAbort ? Math.max(1, Number(httpMs) || 30000) : 0;
      const controller = useAbort ? new AbortController() : null;
      const abortTimer = controller
        ? setTimeout(() => controller.abort(), httpTimeout)
        : null;
      let escalouPusherPeloTimeout = false;
      try {
        const response = await this.sendMessage(combinedText, allFiles, {
          signal: controller && controller.signal,
        });
        if (abortTimer) {
          clearTimeout(abortTimer);
        }
        if (this.isPendenteRespostaWebsocket(response)) {
          this.requisicaoPendenteId = this.extrairRequisicaoId(response);
          this.iniciarEsperaRespostaPusher();
        } else {
          this.requisicaoPendenteId = null;
          this.applyDifyResponseFromJson(response);
        }
      } catch (err) {
        if (abortTimer) {
          clearTimeout(abortTimer);
        }
        const isAbort = err
          && (err.name === 'AbortError' || (typeof err.message === 'string' && err.message.includes('aborted')));
        if (isAbort && this.pusherHabilitado) {
          escalouPusherPeloTimeout = true;
          this.iniciarEsperaRespostaPusher();
        } else {
          this.conversaEncerrada = false;
          this.chamadoDeskLink = null;
          this.messages.push({
            role: 'bot',
            text: 'Erro ao conectar. Verifique se a URL da API está correta.',
          });
        }
      } finally {
        if (!escalouPusherPeloTimeout && !this.aguardandoRespostaPusher) {
          this.loading = false;
        }
      }
    },
    mergePostPayload(res) {
      if (!res || typeof res !== 'object') return {};
      if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
        return { ...res, ...res.data };
      }
      return res;
    },
    isPendenteRespostaWebsocket(res) {
      const o = this.mergePostPayload(res);
      if (!o.aceito) return false;
      if (o.processando !== true) return false;
      return !!(o.requisicao_id || o.requisicaoId);
    },
    extrairRequisicaoId(res) {
      const o = this.mergePostPayload(res);
      return o.requisicao_id || o.requisicaoId || null;
    },
    parseJsonSeString(val) {
      if (val == null) return val;
      if (typeof val !== 'string' || !val.length) return val;
      if (val[0] === '{' || val[0] === '[') {
        try {
          return JSON.parse(val);
        } catch (e) {
          return val;
        }
      }
      return val;
    },
    normalizarPayloadPusherLaravel(raw) {
      let p = this.parseJsonSeString(raw);
      if (p == null) return p;
      if (typeof p !== 'object' || Array.isArray(p)) return p;
      let d = 0;
      while (d < 4 && p && typeof p === 'object' && typeof p.data === 'string') {
        p = { ...p, data: this.parseJsonSeString(p.data) };
        d += 1;
      }
      return p;
    },
    extrairDadosMensagemPusher(envelope) {
      if (envelope == null) return null;
      if (envelope && typeof envelope === 'object' && !Array.isArray(envelope) && envelope.data != null) {
        return typeof envelope.data === 'object' && !Array.isArray(envelope.data) ? envelope.data : envelope;
      }
      return envelope;
    },
    applyDifyResponseFromJson(response) {
      const data = response?.data ?? response;
      const textRes = data?.message ?? response?.text;
      const inatividadeSyncApi = !!data?.inatividade_ja_processada && !!data?.conversa_encerrada;

      if (inatividadeSyncApi) {
        this.conversaEncerrada = true;
        this.conversaEncerradaPorInatividade = true;
        this.conversationId = null;
        this.chamadoDeskLink = this.pickChamadoDeskLink(data);
        this.$emit('conversa-inatividade', {
          ...data,
          inatividade_sync: true,
        });
        this.$emit('conversa-encerrada', true);
        const botText = textRes || this.conversaEncerradaInatividadeTexto;
        this.messages.push({
          role: 'bot',
          text: botText,
          html: data?.message || null,
        });
        return;
      }
      if (textRes) {
        this.conversaEncerrada = !!data?.conversa_encerrada;
        if (this.conversaEncerrada) {
          this.conversaEncerradaPorInatividade = false;
          this.chamadoDeskLink = this.pickChamadoDeskLink(data);
          this.$emit('conversa-encerrada', true);
        } else {
          this.chamadoDeskLink = null;
        }
        this.messages.push({
          role: 'bot',
          text: textRes,
          html: data?.message || null,
        });
        if (data?.conversation_id) {
          this.conversationId = data.conversation_id;
        }
        return;
      }
      this.conversaEncerrada = false;
      this.chamadoDeskLink = null;
      this.messages.push({
        role: 'bot',
        text: 'Desculpe, não consegui processar sua mensagem no momento.',
      });
    },
    iniciarEsperaRespostaPusher() {
      this.cancelPusherRespostaPendente({
        clearTtl: true, keepLoading: true, clearRequisicao: false,
      });
      if (!this.pusherHabilitado || typeof window === 'undefined' || !window.$canalUsuario) {
        this.requisicaoPendenteId = null;
        this.conversaEncerrada = false;
        this.chamadoDeskLink = null;
        this.messages.push({
          role: 'bot',
          text: 'Não foi possível conectar em tempo real. Abra o chat de novo em instantes ou tente outra conexão.',
        });
        this.loading = false;
        return;
      }
      this.aguardandoRespostaPusher = true;
      this.unsubscribePusherRespostaAssincrona();
      this.pusherRespostaAssincronaHandler = (pusherData) => {
        this.onPusherRespostaAssincrona(pusherData);
      };
      try {
        window.$canalUsuario.bind(this.pusherMensagemRespostaEvento, this.pusherRespostaAssincronaHandler);
      } catch (e) {
        this.requisicaoPendenteId = null;
        this.aguardandoRespostaPusher = false;
        this.messages.push({
          role: 'bot',
          text: 'Não foi possível escutar a resposta. Tente novamente.',
        });
        this.loading = false;
        return;
      }
      const ttl = Math.max(0, Number(this.pusherRespostaAssincronaTtlMs) || 0) || 300000;
      this.pusherRespostaAssincronaTtlTimer = setTimeout(() => {
        this.pusherRespostaAssincronaTtlTimer = null;
        if (!this.aguardandoRespostaPusher) {
          return;
        }
        this.unsubscribePusherRespostaAssincrona();
        this.requisicaoPendenteId = null;
        this.aguardandoRespostaPusher = false;
        this.loading = false;
        this.messages.push({
          role: 'bot',
          text: 'A resposta demorou além do esperado. Tente enviar a mensagem novamente.',
        });
      }, ttl);
    },
    onPusherRespostaAssincrona(pusherData) {
      if (!this.aguardandoRespostaPusher) {
        return;
      }
      const envelope = this.normalizarPayloadPusherLaravel(pusherData);
      if (envelope == null) {
        return;
      }
      const data = this.extrairDadosMensagemPusher(envelope);
      if (data == null) {
        return;
      }
      const eventReq = data && (data.requisicao_id ?? data.requisicaoId);
      if (this.requisicaoPendenteId != null) {
        if (eventReq == null || String(eventReq) !== String(this.requisicaoPendenteId)) {
          return;
        }
      } else {
        const convId = data && (data.conversation_id ?? data.conversationId);
        if (convId && this.conversationId && convId !== this.conversationId) {
          return;
        }
      }
      if (this.pusherRespostaAssincronaTtlTimer) {
        clearTimeout(this.pusherRespostaAssincronaTtlTimer);
        this.pusherRespostaAssincronaTtlTimer = null;
      }
      this.unsubscribePusherRespostaAssincrona();
      this.requisicaoPendenteId = null;
      this.aguardandoRespostaPusher = false;
      this.applyDifyResponseFromJson(envelope);
      this.loading = false;
    },
    cancelPusherRespostaPendente({ clearTtl, keepLoading, clearRequisicao } = {}) {
      if (clearTtl && this.pusherRespostaAssincronaTtlTimer) {
        clearTimeout(this.pusherRespostaAssincronaTtlTimer);
        this.pusherRespostaAssincronaTtlTimer = null;
      }
      this.unsubscribePusherRespostaAssincrona();
      this.aguardandoRespostaPusher = false;
      if (clearRequisicao !== false) {
        this.requisicaoPendenteId = null;
      }
      if (keepLoading !== true && this.loading) {
        this.loading = false;
      }
    },
    unsubscribePusherRespostaAssincrona() {
      if (typeof window === 'undefined' || !window.$canalUsuario || !this.pusherRespostaAssincronaHandler) {
        this.pusherRespostaAssincronaHandler = null;
        return;
      }
      try {
        window.$canalUsuario.unbind(this.pusherMensagemRespostaEvento, this.pusherRespostaAssincronaHandler);
      } catch (e) {
        /* ignored */
      }
      this.pusherRespostaAssincronaHandler = null;
    },
    async sendMessage(text, files = [], options = {}) {
      // eslint-disable-next-line no-underscore-dangle
      const baseUrl = this.apiUrl || (typeof window !== 'undefined' && window.__OG_CHATBOT_API_URL__) || '';
      const url = baseUrl ? `${baseUrl.replace(/\/$/, '')}${this.chatEndpoint}` : null;

      if (!url) {
        return { text: 'Chat em modo de demonstração. Configure apiUrl para conectar ao backend.' };
      }

      const { signal } = options;

      let finalUrl = url;
      const token = this.apiToken
        || (typeof window !== 'undefined' && window.__OG_CHATBOT_API_TOKEN__) // eslint-disable-line no-underscore-dangle
        || (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('@ERP:api_token'));
      if (token) {
        const sep = url.includes('?') ? '&' : '?';
        finalUrl = `${url}${sep}api_token=${encodeURIComponent(token)}`;
      }

      let res;
      if (files && files.length > 0) {
        const form = new FormData();
        form.append('message', text);
        if (this.conversationId) form.append('conversation_id', this.conversationId);
        files.forEach((file) => {
          form.append('files[]', file);
          let type = 'document';
          if (file.type?.startsWith('audio/')) type = 'audio';
          else if (file.type?.startsWith('image/')) type = 'image';
          form.append('file_types[]', type);
        });
        res = await fetch(finalUrl, { method: 'POST', body: form, signal });
      } else {
        const body = { message: text };
        if (this.conversationId) body.conversation_id = this.conversationId;
        res = await fetch(finalUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        });
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      return res.json();
    },
    subscribePusherChatInatividade() {
      if (!this.pusherHabilitado || typeof window === 'undefined' || !window.$canalUsuario) {
        return;
      }
      this.unsubscribePusherChatInatividade();
      this.pusherChatInatividadeHandler = (data) => {
        this.onChatInatividadePusher(data);
      };
      window.$canalUsuario.bind(this.pusherEventoChatInatividade, this.pusherChatInatividadeHandler);
    },
    unsubscribePusherChatInatividade() {
      if (typeof window === 'undefined' || !window.$canalUsuario || !this.pusherChatInatividadeHandler) {
        return;
      }
      try {
        window.$canalUsuario.unbind(this.pusherEventoChatInatividade, this.pusherChatInatividadeHandler);
      } catch (e) {
        /* ignored */
      }
      this.pusherChatInatividadeHandler = null;
    },
    onChatInatividadePusher(data) {
      const envelope = this.normalizarPayloadPusherLaravel(data);
      const payload = envelope != null
        ? this.extrairDadosMensagemPusher(envelope)
        : null;
      const convId = payload && (payload.conversation_id ?? payload.conversationId);
      if (convId && this.conversationId && convId !== this.conversationId) {
        return;
      }
      this.cancelPusherRespostaPendente({ clearTtl: true, keepLoading: false });
      if (this.pendingBatchTimer) {
        clearTimeout(this.pendingBatchTimer);
        this.pendingBatchTimer = null;
      }
      this.pendingBatch = [];
      this.conversationId = null;
      this.conversaEncerrada = true;
      this.conversaEncerradaPorInatividade = true;
      this.chamadoDeskLink = this.pickChamadoDeskLink(payload);
      this.$emit('conversa-inatividade', data || {});
      this.$emit('conversa-encerrada', true);
    },
  },
  created() {
    this.subscribePusherChatInatividade();
  },
  beforeDestroy() {
    this.unsubscribePusherChatInatividade();
    this.cancelPusherRespostaPendente({ clearTtl: true, keepLoading: true });
    if (this.pendingBatchTimer) {
      clearTimeout(this.pendingBatchTimer);
      this.pendingBatchTimer = null;
    }
    if (this.pendingBatch.length > 0) {
      this.flushPendingBatch().catch(() => {});
    }
    if (this.layout === 'sidebar' && !this.embedded && typeof document !== 'undefined') {
      document.body.classList.remove('og-chatbot-sidebar-open');
    }
  },
};
</script>

<style lang="scss" scoped src="./ChatbotWidget.scss"></style>
<style lang="scss" src="./ChatbotWidget-global.scss"></style>
